
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scale, Shield, FileText, AlertTriangle, Cookie, Lock, BookOpen, Mail, CheckCircle2, Home, LayoutDashboard } from 'lucide-react';
import { LegalPortalCard } from '@/components/legal/LegalPortalCard';
import { ComplianceBadge } from '@/components/legal/ComplianceBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { images } from '@/assets/images';

const Legal: React.FC = () => {
  const navigate = useNavigate();
  
  const legalDocuments = [
    {
      title: 'Terms of Service',
      description: 'Our terms and conditions for using EquiForge™ Homebuyer Optimizer',
      icon: FileText,
      href: '/terms-of-service',
      estimatedReadTime: '8 min read',
      accentColor: 'hsl(var(--chart-1))'
    },
    {
      title: 'Privacy Policy',
      description: 'How we collect, use, and protect your personal information',
      icon: Shield,
      href: '/privacy',
      estimatedReadTime: '10 min read',
      accentColor: 'hsl(var(--chart-2))'
    },
    {
      title: 'End User License Agreement',
      description: 'Software licensing terms and usage restrictions for the application',
      icon: Lock,
      href: '/eula',
      estimatedReadTime: '12 min read',
      accentColor: 'hsl(var(--chart-3))'
    },
    {
      title: 'Cookie Policy',
      description: 'Information about cookies and tracking technologies we use',
      icon: Cookie,
      href: '/cookie-policy',
      estimatedReadTime: '6 min read',
      accentColor: 'hsl(var(--chart-4))'
    },
    {
      title: 'Disclaimers',
      description: 'Important disclaimers about our financial calculators and tools',
      icon: AlertTriangle,
      href: '/disclaimers',
      estimatedReadTime: '5 min read',
      accentColor: 'hsl(var(--chart-5))'
    },
    {
      title: 'User Agreement',
      description: 'Agreement terms you accepted when creating your account',
      icon: BookOpen,
      href: '/agreement',
      estimatedReadTime: '7 min read',
      accentColor: 'hsl(var(--primary))'
    }
  ];

  const complianceStandards = [
    { name: 'SOC 2 Type II', certified: true },
    { name: 'ISO 27001', certified: true },
    { name: 'ISO 42001 (AI)', certified: true },
    { name: 'PCI-DSS', certified: true },
    { name: 'GDPR', certified: true },
    { name: 'CCPA/CPRA', certified: true },
    { name: 'COPPA', certified: true },
    { name: 'HIPAA', certified: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <div className="flex justify-center pt-6 mb-2">
        <Link to="/dashboard" className="hover:scale-105 transition-transform duration-300">
          <img
            src={images.equiforgeLogoFlat}
            alt="EquiForge"
            className="h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
          />
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <motion.div 
          className="container mx-auto max-w-6xl relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Scale className="w-10 h-10 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Legal Center
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Access our comprehensive legal documentation, privacy policies, and compliance information
            </p>
            <div className="flex justify-center mt-6">
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="gap-2"
              >
                <Home className="w-4 h-4" />
                Return to Dashboard
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Compliance Badges */}
      <section className="py-8 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-center text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-6">
              Security & Compliance Standards
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {complianceStandards.map((standard, index) => (
                <ComplianceBadge 
                  key={standard.name}
                  name={standard.name}
                  certified={standard.certified}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legal Documents Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-3">Legal Documents</h2>
              <p className="text-muted-foreground">
                Review our policies and agreements to understand your rights and responsibilities
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {legalDocuments.map((doc, index) => (
                <LegalPortalCard
                  key={doc.href}
                  {...doc}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legal Resources */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold text-center mb-8">Legal Resources</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Version History */}
              <Card className="border-2 hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Document Versions</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Track changes and view previous versions of our legal documents
                      </p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span>EULA v1.0 - October 10, 2025</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span>Privacy Policy v2.1 - May 10, 2025</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span>Terms of Service v2.0 - May 10, 2025</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FAQ */}
              <Card className="border-2 hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Legal FAQ</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Common questions about our legal policies and your rights
                      </p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                          <span>How is my data protected?</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                          <span>Can I export my data?</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                          <span>What are my GDPR rights?</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dashboard Link */}
              <Card className="border-2 hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                      <LayoutDashboard className="w-6 h-6 text-cyan-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-2">Return to Dashboard</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Continue your homebuying journey and access all your tools
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate('/dashboard')}
                        className="w-full gap-2"
                      >
                        <Home className="w-4 h-4" />
                        Go to Dashboard
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Legal Team */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold mb-3">Have Legal Questions?</h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Our legal team is here to help. If you have questions about our policies, 
                  data practices, or legal compliance, please reach out.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button size="lg" className="min-w-[200px]">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Legal Team
                  </Button>
                  <div className="text-sm text-muted-foreground">
                    <div>Email: legal@equiforge.com</div>
                    <div>Support: support@equiforge.com</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer Notice */}
      <section className="py-8 px-4 border-t">
        <div className="container mx-auto max-w-6xl text-center text-sm text-muted-foreground">
          <p>
            Last updated: October 10, 2025 • All documents are effective immediately upon posting • 
            <a href="/dashboard" className="text-primary hover:underline ml-1">
              Return to Dashboard
            </a>
            {" • "}
            <a href="/legal-hub" className="text-primary hover:underline">
              Back to Legal Hub
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Legal;
